/**
 * File: searchService.ts
 * Description: Service class for search-related API operations
 * Author: GitHub Copilot
 * Created: 2025-01-03
 * Last Modified: 2025-01-03
 */

import { API_CONFIG } from '../config/api';
import { SearchRequest, SearchResponse, Chunk, SearchResultDocument, SearchOptions, UserDocsSearchRequest, SearchSummaryRequest, SearchSummaryContainer } from '../types/search';
import { logger } from '../utils/logger';

import { apiClient } from './apiClient';
import {
  GENERAL_LINE_OF_BUSINESS,
  KnowledgeBaseDocument,
  KnowledgeBaseService,
} from './knowledgeBaseService';

const SOURCE_GUIDELINES = 'guidelines';
const BROWSE_AUTHOR = 'System';
const BROWSE_DESCRIPTION = 'Guideline document from the knowledge base';
const BROWSE_CHUNK_TYPE = 'text';

export class SearchService {
  /**
   * Search for document chunks using the wiki search endpoint
   * @param searchText - The text to search for
   * @param filters - Optional filters to apply
   * @param generateSummary - Whether to generate summaries for chunks
   * @return Promise with search response containing chunk data
   * @throws ApiError on request failure
   */
  public async searchChunks(
    searchText: string, 
    filters?: SearchRequest['filters'], 
    generateSummary?: boolean,
    useVectorSearch?: boolean,
  ): Promise<SearchResponse> {
    const request: SearchRequest = {
      search_text: searchText,
      filters: filters || [],
      generate_summary: generateSummary,
      use_vector_search: useVectorSearch ?? true,
    };
    
    return apiClient.post<SearchResponse>('/api/search/search_wiki', request);
  }

  /**
   * Search for guidelines using the guidelines search endpoint
   * @param searchText - The text to search for
   * @param filters - Optional filters to apply
   * @param generateSummary - Whether to generate summaries for chunks
   * @return Promise with search response containing guidelines data
   * @throws ApiError on request failure
   */
  public async searchGuidelines(
    searchText: string, 
    filters?: SearchRequest['filters'], 
    generateSummary?: boolean,
    useVectorSearch?: boolean,
  ): Promise<SearchResponse> {
    const request: SearchRequest = {
      search_text: searchText,
      filters: filters || [],
      generate_summary: generateSummary,
      use_vector_search: useVectorSearch ?? true, // Default to true if not specified
    };
    
    return apiClient.post<SearchResponse>('/api/search/search_guidelines', request);
  }

  /**
   * Search for user documents using the user docs search endpoint
   * @param searchText - The text to search for
   * @param projectId - The project ID to filter documents by
   * @param filters - Optional filters to apply
   * @return Promise with search response containing user documents data
   * @throws ApiError on request failure
   */
  public async searchUserDocs(searchText: string, projectId: string, filters?: SearchRequest['filters']): Promise<SearchResponse> {
    const request: UserDocsSearchRequest = {
      search_text: searchText,
      project_id: projectId,
      filters: filters || [],
    };
    
    return apiClient.post<SearchResponse>('/api/search/search_user_docs', request);
  }

  /**
   * Generate summaries for selected search results in a second phase
   * @param chunks - Array of chunk data with IDs and content
   * @param characterLimit - Maximum characters for summaries
   * @return Promise with summaries for each chunk
   * @throws ApiError on request failure
   */
  public async generateSummaries(
    chunks: Array<{ chunk_id: string; content: string }>,
    characterLimit: number = 150,
  ): Promise<SearchSummaryContainer> {
    const request: SearchSummaryRequest = {
      chunks,
      character_limit: characterLimit,
    };
    
    return apiClient.post<SearchSummaryContainer>('/api/search/generate_summaries', request);
  }

  /**
   * Generate a single summary for one document
   * @param document - The document to generate summary for
   * @param characterLimit - Maximum characters for summary
   * @return Promise with updated document containing summary
   */
  public async generateSingleSummary(
    document: SearchResultDocument,
    characterLimit: number = 150,
  ): Promise<SearchResultDocument | null> {
    try {
      if (!document.chunks || document.chunks.length === 0) {
        return null;
      }

      const primaryChunk = document.chunks[0];
      const content = primaryChunk.chunked_content || (primaryChunk as any).content || '';
      
      if (!content?.trim()) {
        return null;
      }
      
      
      // Call backend with single chunk
      const summaryResponse = await this.generateSummaries([{
        chunk_id: primaryChunk.chunk_id,
        content,
      }], characterLimit);

      if (summaryResponse.summaries.length > 0) {
        const summary = summaryResponse.summaries[0].summary;
        
        return {
          ...document,
          summary,
          description: summary,
        };
      }

      return null;
    } catch (error) {
      logger.error(`SearchService: Error generating summary for ${document.name}:`, error);
      return null;
    }
  }

  /**
   * Generate summaries for a batch of filtered search results
   * Call this after the frontend has filtered the results from searchKnowledgeBase
   * @param documents - The documents to generate summaries for
   * @param characterLimit - Maximum characters for summaries
   * @return Promise with updated documents containing real summaries
   */
  public async generateSummariesForResults(
    documents: SearchResultDocument[],
    characterLimit: number = 150,
  ): Promise<SearchResultDocument[]> {
    
    try {
      // Prepare chunks for summary generation
      const chunksForSummary: Array<{ chunk_id: string; content: string }> = [];
      
      documents.forEach(document => {
        if (document.chunks && document.chunks.length > 0) {
          // Use the first chunk's content for summary (or combine multiple chunks if needed)
          const primaryChunk = document.chunks[0];
          const content = primaryChunk.chunked_content || (primaryChunk as any).content || '';
          
          
          if (content?.trim()) {
            chunksForSummary.push({
              chunk_id: primaryChunk.chunk_id,
              content,
            });
          }
        }
      });
      
      
      if (chunksForSummary.length === 0) {
        return documents; // Return unchanged if no content to summarize
      }
      
      
      // Call the backend to generate summaries
      const summaryResponse = await this.generateSummaries(chunksForSummary, characterLimit);
      
      
      // Create a map of chunk_id to summary for quick lookup
      const summaryMap = new Map<string, string>();
      summaryResponse.summaries.forEach(summaryItem => {
        summaryMap.set(summaryItem.chunk_id, summaryItem.summary);
      });
      
      // Update documents with real summaries
      const updatedDocuments = documents.map(document => {
        if (document.chunks && document.chunks.length > 0) {
          const primaryChunk = document.chunks[0];
          const summary = summaryMap.get(primaryChunk.chunk_id);
          
          if (summary) {
            return {
              ...document,
              summary,
              description: summary, // Use summary for description too
            };
          }
        }
        
        return document; // Return unchanged if no summary available
      });
      
      return updatedDocuments;
      
    } catch (error) {
      logger.error('SearchService: Error generating summaries for results:', error);
      // Return original documents if summary generation fails
      return documents;
    }
  }

  /**
   * Enhanced search that combines document and chunk data for UI consumption
   * @param options - Search options including query, filters, and pagination
   * @return Promise with array of enhanced search result documents
   * @throws ApiError on request failure
   */
  public async searchKnowledgeBase(options: SearchOptions): Promise<SearchResultDocument[]> {
    try {
      if (this.isGuidelineBrowseRequest(options)) {
        return await this.browseGuidelines(options.filters);
      }

      const searchSources = options.filters?.searchSources || ['wiki', 'guidelines', 'user_docs'];
      const searchPromises: Promise<SearchResponse>[] = [];
      
      // Phase 1: Fast search WITHOUT expensive summary generation
      // Search wiki (historical SAHLD documents) if included
      if (searchSources.includes('wiki')) {
        const wikiPromise = this.searchChunks(
          options.query, 
          options.filters ? this.convertFiltersToKeyValue(options.filters) : undefined,
          false, // Phase 1: NO summary generation for performance
        );
        searchPromises.push(wikiPromise);
      }
      
      // Search guidelines if included
      if (searchSources.includes('guidelines')) {
        const guidelinesPromise = this.searchGuidelines(
          options.query, 
          options.filters ? this.convertFiltersToKeyValue(options.filters) : undefined,
          false, // Phase 1: NO summary generation for performance
        );
        searchPromises.push(guidelinesPromise);
      }
      
      // Search user docs if included and project_id is provided
      if (searchSources.includes('user_docs') && options.project_id) {
        const userDocsPromise = this.searchUserDocs(
          options.query,
          options.project_id,
          options.filters ? this.convertFiltersToKeyValue(options.filters) : undefined,
        );
        searchPromises.push(userDocsPromise);
      }
      
      // Wait for all searches to complete (handle individual failures)
      const searchResponses = await Promise.allSettled(searchPromises);
      
      
      // Combine all search response items from successful responses only
      const allSearchItems: any[] = [];
      searchResponses.forEach((response, index) => {
        if (response.status === 'fulfilled' && response.value.search_response_items) {
          // Tag items with their source for debugging
          const taggedItems = response.value.search_response_items.map(item => ({
            ...item,
            searchSource: searchSources[index], // Add source tag for debugging,
          }));
          allSearchItems.push(...taggedItems);
        } else if (response.status === 'rejected') {
          logger.warn(`SearchService: Search ${index} (${searchSources[index]}) failed:`, response.reason);
        }
      });
      
      
      // Sort all items by search score (highest first)
      allSearchItems.sort((a, b) => (b.search_score || 0) - (a.search_score || 0));
      
      // Group chunks by document and convert to SearchResultDocument format
      const documentsMap = new Map<string, SearchResultDocument>();        allSearchItems.forEach(item => {
        // Extract chunk data from the data field
        const chunk = item.data as Chunk;
        if (!chunk) {
          return;
        }
        
        const documentId = chunk.document_id || chunk.path || chunk.chunk_id;
        const documentTitle = chunk.document_title || item.document_title || 'Untitled Document';
        
        if (!documentsMap.has(documentId)) {
          // Create new document entry WITHOUT backend summary (Phase 1)
          // Summaries will be generated in Phase 2 via /generate_summaries endpoint
          const chunkContent = chunk.chunked_content || (chunk as any).content || '';
          
          // Generate a snippet from chunk content if no backend summary
          const contentSnippet = chunk.summary?.trim() 
            ? chunk.summary 
            : this.generateSummary(chunkContent);
          
          const document: SearchResultDocument = {
            id: documentId,
            name: documentTitle,
            type: this.getTypeForSource(item.searchSource, chunk.sector),
            category: this.getCategoryForSource(item.searchSource, chunk.sector),
            size: chunkContent.length * 2, // Estimate size
            lastModified: chunk.change_date ? new Date(chunk.change_date) : new Date(),
            author: 'System', // Not available in chunk data
            description: contentSnippet,
            tags: chunk.keywords || [],
            path: chunk.path || chunk.url || item.path || `/documents/${documentId}`,
            wiki_path: item.wiki_path, // Add wiki_path from backend
            // Use search_score from the backend as relevanceScore
            relevanceScore: item.search_score || (() => {
              logger.warn('SearchService: Missing search_score for item:', item);
              return 0;
            })(),
            summary: contentSnippet,
            snippet: undefined, // Don't generate snippet for knowledge base searches
            source: item.searchSource, // Add source from search response
            chunks: [chunk],
            // Store additional search metadata
            searchMetadata: {
              searchScore: item.search_score,
              rerankerScore: item.reranker_score,
              searchHighlights: item.search_highlights,
              searchCaptions: item.search_captions,
            },
          };
          
          documentsMap.set(documentId, document);
        } else {
          // Add chunk to existing document
          const existingDocument = documentsMap.get(documentId)!;
          existingDocument.chunks!.push(chunk);

          // Update summary and description if the new chunk has a better backend summary
          if (chunk.summary?.trim()) {
            const newSummary = this.generateSummary(chunk.chunked_content, chunk.summary);
            existingDocument.summary = newSummary;
            existingDocument.description = newSummary; // Use summary for description too
          }

          // Merge keywords
          const newKeywords = chunk.keywords.filter(
            (keyword: string) => !existingDocument.tags.includes(keyword),
          );
          existingDocument.tags.push(...newKeywords);

          // Update size estimate
          existingDocument.size += chunk.chunked_content.length * 2;
            
          // Update relevance score to use the highest search score
          const currentSearchScore = item.search_score || 0;
          const existingSearchScore = existingDocument.searchMetadata?.searchScore || 0;
          if (currentSearchScore > existingSearchScore) {
            existingDocument.relevanceScore = currentSearchScore;
            existingDocument.searchMetadata = {
              searchScore: item.search_score,
              rerankerScore: item.reranker_score,
              searchHighlights: item.search_highlights,
              searchCaptions: item.search_captions,
            };  
          }
        }
      });
      
      // Convert map to array and apply sorting by relevance score
      let results = Array.from(documentsMap.values());
      
      // Apply sorting (already sorted by relevance from the combined search items)
      results = this.sortResults(results, options.sortBy || 'relevance', options.sortOrder);
      
      // Apply pagination
      if (options.offset !== undefined && options.limit !== undefined) {
        const start = options.offset;
        const end = start + options.limit;
        results = results.slice(start, end);
      } else if (options.limit !== undefined) {
        results = results.slice(0, options.limit);
      }
      
      return results;
    } catch (error) {
      logger.error('Error searching knowledge base:', error);
      throw error;
    }
  }

  /**
   * Whether a request should list guidelines from blob storage instead of
   * running a ranked search (empty query scoped to guidelines only)
   * @param options - The query and filters of the request
   * @return True when the guidelines should be browsed
   */
  public isGuidelineBrowseRequest(
    options: { query: string; filters?: SearchOptions['filters'] },
  ): boolean {
    const sources = options.filters?.searchSources;
    return (
      !options.query.trim() &&
      sources?.length === 1 &&
      sources[0] === SOURCE_GUIDELINES
    );
  }

  /**
   * List every guideline in the knowledge base blob store, optionally limited
   * to the selected lines of business
   * @param filters - Optional filters; only lineOfBusiness is applied
   * @return Promise with one document per guideline file
   */
  public async browseGuidelines(
    filters?: SearchOptions['filters'],
  ): Promise<SearchResultDocument[]> {
    const selectedLobs = filters?.lineOfBusiness ?? [];
    const documents = await KnowledgeBaseService.listDocuments();

    return documents
      .filter(
        (doc) =>
          selectedLobs.length === 0 ||
          selectedLobs.includes(doc.line_of_business ?? GENERAL_LINE_OF_BUSINESS),
      )
      .map((doc) => this.convertKnowledgeBaseDocument(doc));
  }

  private convertKnowledgeBaseDocument(doc: KnowledgeBaseDocument): SearchResultDocument {
    // The browser filters guidelines by chunk sector, so carry the LOB there.
    const chunk: Chunk = {
      chunk_id: doc.id,
      document_id: doc.id,
      chunked_content: '',
      chunk_type: BROWSE_CHUNK_TYPE,
      document_title: doc.name,
      keywords: [],
      sector: doc.line_of_business ?? GENERAL_LINE_OF_BUSINESS,
      chunk_order: 0,
    };

    return {
      id: doc.id,
      name: doc.name,
      type: this.getTypeForSource(SOURCE_GUIDELINES),
      category: this.getCategoryForSource(SOURCE_GUIDELINES),
      size: 0,
      lastModified: doc.last_modified ? new Date(doc.last_modified) : new Date(),
      author: BROWSE_AUTHOR,
      description: doc.description ?? BROWSE_DESCRIPTION,
      tags: doc.keywords ?? [],
      path: doc.path ?? doc.id,
      source: SOURCE_GUIDELINES,
      chunks: [chunk],
    };
  }

  /**
   * Convert SearchFilters to KeyValue array for API
   * @param filters - The filters to convert
   * @return Array of key-value pairs
   */
  private convertFiltersToKeyValue(filters: SearchOptions['filters']): SearchRequest['filters'] {
    const keyValues: SearchRequest['filters'] = [];
    
    if (!filters) {return keyValues;}
    
    if (filters.documentTypes?.length) {
      filters.documentTypes.forEach(type => {
        keyValues!.push({ key: 'document_type', value: type });
      });
    }
    
    if (filters.categories?.length) {
      filters.categories.forEach(category => {
        keyValues!.push({ key: 'category', value: category });
      });
    }
    
    if (filters.sectors?.length) {
      filters.sectors.forEach(sector => {
        keyValues!.push({ key: 'sector', value: sector });
      });
    }
    
    if (filters.dateRange) {
      keyValues!.push({ 
        key: 'date_start', 
        value: filters.dateRange.start.toISOString(),
      });
      keyValues!.push({ 
        key: 'date_end', 
        value: filters.dateRange.end.toISOString(),
      });
    }
    
    if (filters.lineOfBusiness?.length) {
      filters.lineOfBusiness.forEach(lob => {
        keyValues!.push({ key: 'line_of_business', value: lob });
      });
    }
    
    return keyValues;
  }

  /**
   * Get appropriate category based on search source
   * @param searchSource - The source of the search result
   * @param sector - The sector from chunk data
   * @return Category string
   */
  private getCategoryForSource(searchSource?: string, sector?: string): string {
    if (searchSource === 'wiki') {
      return 'SAHLD / DSI';
    }
    if (searchSource === 'guidelines') {
      return 'Guidelines & Standards';
    }
    if (searchSource === 'user_docs') {
      return 'User Documents';
    }
    return sector || 'General';
  }

  /**
   * Get document type based on search source and sector
   * @param searchSource - The source of the search (wiki, guidelines, user_docs)
   * @param sector - The sector from chunk data
   * @return Type string
   */
  private getTypeForSource(searchSource?: string, sector?: string): string {
    if (searchSource === 'wiki') {
      return 'Legacy Documentation';
    }
    if (searchSource === 'guidelines') {
      return 'Standard';
    }
    if (searchSource === 'user_docs') {
      return 'Other';
    }
    return 'Other';
  }

  /**
   * Generate a summary from content or use backend summary if available
   * @param content - The content to summarize
   * @param backendSummary - Summary from backend if available
   * @return Summary text
   */
  private generateSummary(content: string, backendSummary?: string): string {
    // Use backend summary if available
    if (backendSummary?.trim()) {
      return backendSummary;
    }
    
    // Fallback to client-side summary generation
    // Extract first sentence or up to 150 characters
    const sentences = content.split('. ');
    if (sentences.length > 0 && sentences[0].length <= 150) {
      return `${sentences[0]}.`;
    }
    
    return `${content.substring(0, 150)}...`;
  }

  /**
   * Generate a snippet highlighting query terms
   * @param content - The content to create snippet from
   * @param query - The search query
   * @return Snippet with context around query terms
   */
  private generateSnippet(content: string, query: string): string {
    const queryWords = query.toLowerCase().split(' ').filter(word => word.length > 2);
    const contentLower = content.toLowerCase();
    
    // Find first occurrence of any query word
    let earliestIndex = content.length;
    queryWords.forEach(word => {
      const index = contentLower.indexOf(word);
      if (index !== -1 && index < earliestIndex) {
        earliestIndex = index;
      }
    });
    
    if (earliestIndex === content.length) {
      // No query words found, return beginning of content
      return `${content.substring(0, 100)}...`;
    }
    
    // Get context around the match
    const start = Math.max(0, earliestIndex - 50);
    const end = Math.min(content.length, earliestIndex + 150);
    
    let snippet = content.substring(start, end);
    if (start > 0) {snippet = `...${snippet}`;}
    if (end < content.length) {snippet = `${snippet}...`;}
    
    return snippet;
  }

  /**
   * Sort search results
   * @param results - The results to sort
   * @param sortBy - Sort criteria
   * @param sortOrder - Sort order
   * @return Sorted results
   */
  private sortResults(
    results: SearchResultDocument[], 
    sortBy?: SearchOptions['sortBy'], 
    sortOrder: SearchOptions['sortOrder'] = 'desc',
  ): SearchResultDocument[] {
    const multiplier = sortOrder === 'asc' ? 1 : -1;
    
    return results.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return multiplier * a.name.localeCompare(b.name);
        case 'date':
          return multiplier * (a.lastModified.getTime() - b.lastModified.getTime());
        case 'relevance':
        default:
          return multiplier * ((a.relevanceScore || 0) - (b.relevanceScore || 0));
      }
    });
  }
}

export const searchService = new SearchService();
